import { DiagramComponent } from '../../core/DiagramComponent';
import type { DotPoolConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

/** Internal state for a bin */
interface BinState {
  id: string;
  cx: number;
  cy: number;
  width: number;
  height: number;
  elements: JXGElement[];
  dotIds: string[];
  countLabel?: JXGElement;  // Dynamic count label
}

/** Internal state for a dot */
interface DotState {
  id: string;
  point: JXGElement;
  currentBinId: string;
}

/**
 * DotPool Component - A fully declarative drag-and-drop dot distribution system
 * 
 * Creates a pool of draggable dots in a source bin that can be distributed
 * to multiple target bins. All behavior is automatic based on the config.
 */
class DotPool extends DiagramComponent<DotPoolConfig> {
  private sourceBin: BinState | null = null;
  private targetBins: BinState[] = [];
  private dots: DotState[] = [];
  private lastCount: number = 0;
  private lastTargetCount: number = 0;
  private successLabel: JXGElement | null = null;
  private sourceCountLabel: JXGElement | null = null;

  render(): JXGElement[] {
    const count = Number(this.evaluateExpression(this.config.count)) || 0;
    const targetCount = Number(this.evaluateExpression(this.config.targets.count)) || 0;
    
    this.lastCount = count;
    this.lastTargetCount = targetCount;

    // Create source bin
    this.createSourceBin();
    
    // Create target bins
    this.createTargetBins(targetCount);
    
    // Create dots in source bin
    this.createDots(count);
    
    // Create success indicator
    this.createSuccessIndicator();
    
    // Initial count update
    this.updateAllCounts();

    return this.elements;
  }

  private createSourceBin(): void {
    const src = this.config.source;
    const cx = Number(this.evaluateExpression(src.position[0]));
    const cy = Number(this.evaluateExpression(src.position[1]));
    const width = src.width;
    const height = src.height;
    const color = src.color || '#3498db';
    const label = src.label ? String(this.evaluateExpression(src.label)) : '';

    const binElements = this.createBinVisual(cx, cy, width, height, color, label);
    
    this.sourceBin = {
      id: 'source',
      cx, cy, width, height,
      elements: binElements,
      dotIds: []
    };
    
    binElements.forEach(el => this.elements.push(el));
    
    // Add count label below source bin (if enabled)
    const showCounts = this.config.showCounts !== false; // default true
    if (showCounts) {
      const halfH = height / 2;
      this.sourceCountLabel = this.board.create('text', [cx, cy - halfH - 0.6, ''], {
        fontSize: 16,
        anchorX: 'middle',
        anchorY: 'top',
        fixed: true,
        cssClass: 'jxg-text-no-pointer'
      });
      this.elements.push(this.sourceCountLabel);
    }
  }

  private createTargetBins(count: number): void {
    const cfg = this.config.targets;
    const startX = cfg.startPosition[0];
    const startY = cfg.startPosition[1];
    const width = cfg.width;
    const height = cfg.height;
    const color = cfg.color || '#27ae60';
    const columns = cfg.columns || 5;
    const spacingX = cfg.spacingX || 3.5;
    const spacingY = cfg.spacingY || 4;
    const labelTemplate = cfg.labelTemplate || '';
    
    // Get expected count per bin (k)
    const expectedPerBin = Number(this.evaluateExpression('${k}')) || 0;

    for (let i = 0; i < count; i++) {
      const col = i % columns;
      const row = Math.floor(i / columns);
      const cx = startX + col * spacingX;
      const cy = startY - row * spacingY;
      
      // Evaluate label template with 'i' in context
      let label = '';
      if (labelTemplate) {
        const labelContext = { ...this.getState(), i };
        label = String(this.evaluateExpressionWithContext(labelTemplate, labelContext));
      }

      const binElements = this.createBinVisual(cx, cy, width, height, color, label);
      
      // Add count label below this target bin (if enabled)
      const showCounts = this.config.showCounts !== false; // default true
      let countLabel: JXGElement | undefined;
      if (showCounts) {
        const halfH = height / 2;
        countLabel = this.board.create('text', [cx, cy - halfH - 0.4, ''], {
          fontSize: 14,
          anchorX: 'middle',
          anchorY: 'top',
          fixed: true,
          cssClass: 'jxg-text-no-pointer'
        });
        binElements.push(countLabel);
      }
      
      const binState: BinState = {
        id: `target-${i}`,
        cx, cy, width, height,
        elements: binElements,
        dotIds: [],
        countLabel: countLabel
      };
      
      this.targetBins.push(binState);
      binElements.forEach(el => this.elements.push(el));
    }
  }

  private createBinVisual(cx: number, cy: number, width: number, height: number, color: string, label: string): JXGElement[] {
    const elements: JXGElement[] = [];
    const halfW = width / 2;
    const halfH = height / 2;

    // Corner points (invisible)
    const p1 = this.board.create('point', [cx - halfW, cy + halfH], { visible: false, withLabel: false, name: '' });
    const p2 = this.board.create('point', [cx + halfW, cy + halfH], { visible: false, withLabel: false, name: '' });
    const p3 = this.board.create('point', [cx + halfW, cy - halfH], { visible: false, withLabel: false, name: '' });
    const p4 = this.board.create('point', [cx - halfW, cy - halfH], { visible: false, withLabel: false, name: '' });

    // Polygon
    const poly = this.board.create('polygon', [p1, p2, p3, p4], {
      strokeColor: color,
      strokeWidth: 3,
      fillColor: color,
      fillOpacity: 0.1,
      vertices: { visible: false, withLabel: false, name: '' }
    });

    elements.push(p1, p2, p3, p4, poly);

    // Label above bin
    if (label) {
      const labelEl = this.board.create('text', [cx, cy + halfH + 0.4, label], {
        fontSize: 14,
        anchorX: 'middle',
        anchorY: 'bottom',
        fixed: true,
        cssClass: 'jxg-text-no-pointer'
      });
      elements.push(labelEl);
    }

    return elements;
  }

  private createDots(count: number): void {
    const dotColor = this.config.dotColor || '#e74c3c';
    const dotSize = this.config.dotSize || 5;

    for (let i = 0; i < count; i++) {
      const dotId = `dot-${i}`;
      const [x, y] = this.getDotPositionInBin(this.sourceBin!, i, count);

      const point = this.board.create('point', [x, y], {
        size: dotSize,
        fillColor: dotColor,
        strokeColor: dotColor,
        strokeWidth: 2,
        fixed: false,
        withLabel: false,
        name: '',
        highlightFillColor: dotColor,
        highlightStrokeColor: '#000',
        showInfobox: false
      });

      const dotState: DotState = {
        id: dotId,
        point: point,
        currentBinId: 'source'
      };

      // Handle drag end
      point.on('up', () => this.handleDotDrop(dotState));

      this.dots.push(dotState);
      this.sourceBin!.dotIds.push(dotId);
      this.elements.push(point);
    }
  }

  private handleDotDrop(dotState: DotState): void {
    const x = (dotState.point as any).X();
    const y = (dotState.point as any).Y();
    const previousBinId = dotState.currentBinId;

    // Find which bin the dot is in
    let newBinId: string | null = null;

    // Check source bin
    if (this.isInsideBin(x, y, this.sourceBin!)) {
      newBinId = 'source';
    }

    // Check target bins
    if (!newBinId) {
      for (const bin of this.targetBins) {
        if (this.isInsideBin(x, y, bin)) {
          newBinId = bin.id;
          break;
        }
      }
    }

    // If dropped outside all bins, snap back to previous
    if (!newBinId) {
      this.rearrangeDotsInBin(previousBinId);
      return;
    }

    // Update bin assignments if changed
    if (newBinId !== previousBinId) {
      // Remove from previous bin
      const prevBin = this.getBinById(previousBinId);
      if (prevBin) {
        const idx = prevBin.dotIds.indexOf(dotState.id);
        if (idx !== -1) prevBin.dotIds.splice(idx, 1);
      }

      // Add to new bin
      const newBin = this.getBinById(newBinId);
      if (newBin && !newBin.dotIds.includes(dotState.id)) {
        newBin.dotIds.push(dotState.id);
      }

      dotState.currentBinId = newBinId;

      // Rearrange both bins
      this.rearrangeDotsInBin(previousBinId);
      this.rearrangeDotsInBin(newBinId);
    } else {
      // Dropped in same bin - just rearrange
      this.rearrangeDotsInBin(previousBinId);
    }
    
    // Update all count labels
    this.updateAllCounts();
  }

  private isInsideBin(x: number, y: number, bin: BinState): boolean {
    const halfW = bin.width / 2;
    const halfH = bin.height / 2;
    return x >= bin.cx - halfW && x <= bin.cx + halfW &&
           y >= bin.cy - halfH && y <= bin.cy + halfH;
  }

  private getBinById(id: string): BinState | null {
    if (id === 'source') return this.sourceBin;
    return this.targetBins.find(b => b.id === id) || null;
  }

  private rearrangeDotsInBin(binId: string): void {
    const bin = this.getBinById(binId);
    if (!bin) return;

    const dotsInBin = this.dots.filter(d => d.currentBinId === binId);
    dotsInBin.forEach((dotState, index) => {
      const [newX, newY] = this.getDotPositionInBin(bin, index, dotsInBin.length);
      (dotState.point as any).moveTo([newX, newY], 200);
    });
  }

  private getDotPositionInBin(bin: BinState, index: number, total: number): [number, number] {
    if (total === 0 || total === 1) return [bin.cx, bin.cy];

    const margin = 0.4;
    const availableWidth = bin.width - margin * 2;
    const availableHeight = bin.height - margin * 2;

    const cols = Math.ceil(Math.sqrt(total));
    const rows = Math.ceil(total / cols);

    const col = index % cols;
    const row = Math.floor(index / cols);

    const spacingX = cols > 1 ? availableWidth / (cols - 1) : 0;
    const spacingY = rows > 1 ? availableHeight / (rows - 1) : 0;

    const startX = bin.cx - availableWidth / 2;
    const startY = bin.cy + availableHeight / 2;

    const x = cols > 1 ? startX + col * spacingX : bin.cx;
    const y = rows > 1 ? startY - row * spacingY : bin.cy;

    return [x, y];
  }

  /** Evaluate expression with custom context (for label templates) */
  private evaluateExpressionWithContext(template: string, context: Variables): string | number | boolean {
    // Simple ${...} replacement
    return template.replace(/\$\{([^}]+)\}/g, (match, expr) => {
      try {
        // Build safe evaluator
        const keys = Object.keys(context);
        const values = keys.map(k => context[k]);
        const func = new Function(...keys, `return (${expr})`);
        return String(func(...values));
      } catch (e) {
        return match;
      }
    });
  }

  /** Create success indicator that shows when division is complete */
  private createSuccessIndicator(): void {
    const showSuccessIndicator = this.config.showSuccessIndicator !== false; // default true
    if (!showSuccessIndicator) return;
    
    // Position success label in the center-top area
    const cfg = this.config.targets;
    const startX = cfg.startPosition[0];
    const columns = cfg.columns || 5;
    const spacingX = cfg.spacingX || 3.5;
    const centerX = startX + (Math.min(this.lastTargetCount, columns) - 1) * spacingX / 2;
    
    this.successLabel = this.board.create('text', [centerX, 10.5, ''], {
      fontSize: 20,
      anchorX: 'middle',
      anchorY: 'middle',
      fixed: true,
      cssClass: 'jxg-text-no-pointer'
    });
    this.elements.push(this.successLabel);
  }

  /** Update all count labels and success indicator */
  private updateAllCounts(): void {
    const expectedPerBin = Number(this.evaluateExpression('${k}')) || 0;
    const totalDots = this.lastCount;
    
    // Update source bin count
    const sourceCount = this.sourceBin?.dotIds.length || 0;
    if (this.sourceCountLabel) {
      (this.sourceCountLabel as any).setText(`Remaining: ${sourceCount}`);
    }
    
    // Update each target bin count
    let allCorrect = true;
    let totalDistributed = 0;
    
    for (const bin of this.targetBins) {
      const count = bin.dotIds.length;
      totalDistributed += count;
      
      const isCorrect = count === expectedPerBin;
      if (count > 0 && !isCorrect) {
        allCorrect = false;
      }
      if (count === 0) {
        allCorrect = false;
      }
      
      // Update count label with color coding
      if (bin.countLabel) {
        const color = count === 0 ? '#999' : (isCorrect ? '#27ae60' : '#e74c3c');
        const checkMark = isCorrect ? ' ✓' : (count > expectedPerBin ? ' ↑' : (count > 0 ? ' ↓' : ''));
        (bin.countLabel as any).setText(`${count}/${expectedPerBin}${checkMark}`);
        bin.countLabel.setAttribute({ strokeColor: color });
      }
    }
    
    // Update success indicator
    if (this.successLabel) {
      if (allCorrect && sourceCount === 0 && totalDistributed === totalDots) {
        (this.successLabel as any).setText('🎉 Perfect! Each group has exactly ' + expectedPerBin + ' dots!');
        this.successLabel.setAttribute({ strokeColor: '#27ae60' });
      } else if (totalDistributed > 0) {
        const distributed = totalDots - sourceCount;
        (this.successLabel as any).setText(`Distributed: ${distributed}/${totalDots}`);
        this.successLabel.setAttribute({ strokeColor: '#666' });
      } else {
        (this.successLabel as any).setText('Drag dots to the group bins →');
        this.successLabel.setAttribute({ strokeColor: '#999' });
      }
    }
  }

  update(variables: Variables): void {
    this.parentState = { ...this.parentState, ...variables };
    
    const newCount = Number(this.evaluateExpression(this.config.count)) || 0;
    const newTargetCount = Number(this.evaluateExpression(this.config.targets.count)) || 0;
    
    // Only rebuild if counts changed
    if (newCount !== this.lastCount || newTargetCount !== this.lastTargetCount) {
      this.destroy();
      this.sourceBin = null;
      this.targetBins = [];
      this.dots = [];
      this.elements = [];
      this.successLabel = null;
      this.sourceCountLabel = null;
      this.render();
    }
  }
}

export default DotPool;
